import {
  MapIcon,
  FileCheckIcon,
  FileSpreadsheetIcon,
  NotesIcon,
} from "../../../Icons";

/**
 * Single source of truth for General Forms.
 *
 * Toggle `visible` to show/hide a form in the library + submitted list.
 * Backend routes stay intact — visibility is frontend-only.
 * Add a new entry here when introducing a future form.
 *
 * @typedef {Object} GeneralFormDefinition
 * @property {string} id - Stable tab / catalog id
 * @property {string} title - Library card title
 * @property {string} description - Library card subtitle
 * @property {string} label - List tab label
 * @property {import('react').ComponentType} icon
 * @property {string} route - Create path
 * @property {(id: string|number) => string} editRoute
 * @property {string} listKey - Response array key from list API
 * @property {string} apiPath - REST collection path (no leading slash)
 * @property {boolean} visible - When false, hidden from UI (backend kept)
 * @property {boolean} [hasEntries] - Whether list rows include an entries column
 * @property {boolean} [hasFlags] - Whether list rows support Billed/Paid/Faxed/Signed flags
 */

/** @type {GeneralFormDefinition[]} */
export const GENERAL_FORMS_CATALOG = [
  {
    id: "daily-log",
    title: "Daily Log",
    description: "Daily Log Form",
    label: "Daily Log",
    icon: NotesIcon,
    route: "/general-forms/daily-log",
    editRoute: (id) => `/general-forms/daily-log/${id}`,
    listKey: "dailyLogs",
    apiPath: "daily-logs",
    visible: true,
    hasEntries: false,
    hasFlags: true,
  },
  {
    id: "route-sheet",
    title: "Route Sheet",
    description: "Route Sheet Form",
    label: "Route Sheet",
    icon: MapIcon,
    route: "/general-forms/route-sheet",
    editRoute: (id) => `/general-forms/route-sheet/${id}`,
    listKey: "routeSheets",
    apiPath: "route-sheets",
    // Hidden from client UI. Set `visible: true` to restore library, list, and routes.
    visible: false,
    hasEntries: true,
    hasFlags: false,
  },
  {
    id: "patient-consent",
    title: "Patient Consent",
    description: "Patient Consent Form",
    label: "Patient Consent",
    icon: FileCheckIcon,
    route: "/general-forms/patient-consent",
    editRoute: (id) => `/general-forms/patient-consent/${id}`,
    listKey: "patientConsents",
    apiPath: "patient-consents",
    visible: false,
    hasEntries: false,
  },
  {
    id: "medicare",
    title: "Medicare Non-Coverage",
    description: "Notice of Medicare Non-Coverage Form",
    label: "Medicare Non-Coverage",
    icon: FileSpreadsheetIcon,
    route: "/general-forms/medicare-non-coverage",
    editRoute: (id) => `/general-forms/medicare-non-coverage/${id}`,
    listKey: "medicareNonCoverages",
    apiPath: "medicare-non-coverages",
    visible: false,
    hasEntries: false,
  },
];

/** Forms shown in library + list UI. */
export const getVisibleGeneralForms = () =>
  GENERAL_FORMS_CATALOG.filter((form) => form.visible);

export const getGeneralFormById = (id) =>
  GENERAL_FORMS_CATALOG.find((form) => form.id === id) || null;

/** Whether a catalog form is shown in the client library / list / routes. */
export const isGeneralFormVisible = (id) =>
  Boolean(getGeneralFormById(id)?.visible);

export const DEFAULT_GENERAL_FORM_TAB =
  getVisibleGeneralForms()[0]?.id || "daily-log";

export const VISIBLE_GENERAL_FORM_TAB_IDS = new Set(
  getVisibleGeneralForms().map((form) => form.id),
);
